import { Injectable, inject } from '@angular/core';

import { SportsUserSettings } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Observable, combineLatest, map, shareReplay } from 'rxjs';

import { mapEarlyPayoutAcceptance } from '../helpers/early-payout-acceptance';
import { isTurnoverTaxApplied } from '../helpers/is-turnovertax-applied';
import { EarlyPayoutAcceptance } from '../model/early-payout';
import { IConfigSettings, IUserBettingSettings } from '../model/user.models';

/**
 * @whatItDoes Service for managing and accessing sport user settings.
 *
 * @description
 *
 * This service provides observables that supply the user-specific betting and configuration settings. It handles
 * the retrieval of user settings and exposes them in a reactive way.
 *
 * # Note
 *
 * This service should be used in all areas to retrieve user settings, except in the betslip and my-bets modules.
 */

@Injectable({ providedIn: 'root' })
export class BettingSettingsService {
    private readonly sportsUserSettings = inject(SportsUserSettings);

    get bettingSettings$(): Observable<Readonly<IUserBettingSettings>> {
        return this.loadSportsUserSettings$().pipe(map((sportsUserSettings) => sportsUserSettings.bettingSettings));
    }

    get configSettings$(): Observable<Readonly<IConfigSettings>> {
        return this.loadSportsUserSettings$().pipe(map((sportsUserSettings) => sportsUserSettings.configSettings));
    }

    get allSettings$(): Observable<{ bettingSettings: Readonly<IUserBettingSettings>; configSettings: Readonly<IConfigSettings> }> {
        return combineLatest([this.bettingSettings$, this.configSettings$]).pipe(
            map(([bettingSettings, configSettings]) => ({ bettingSettings, configSettings })),
        );
    }

    get isTurnoverTaxApplied$(): Observable<boolean> {
        return this.configSettings$.pipe(map(isTurnoverTaxApplied));
    }

    get earlyPayoutAcceptance$(): Observable<Nullable<EarlyPayoutAcceptance.Any | EarlyPayoutAcceptance.Higher>> {
        return this.bettingSettings$.pipe(map((settings) => mapEarlyPayoutAcceptance(settings)));
    }

    private loadSportsUserSettings$(): Observable<SportsUserSettings> {
        return this.sportsUserSettings.whenReady.pipe(
            map(() => this.sportsUserSettings),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
}
